import React from 'react'
import dayjs from '../../dayjsConfig'
import { currentLocale } from '../../i18nInit'

export const getTallinnTimeZoneOffsetInMilliseconds = (date: Date | number): number => {
  const tallinnTimeZoneOffsetInMinutes = dayjs.utc(new Date(date)).tz('Europe/Tallinn').utcOffset()
  return tallinnTimeZoneOffsetInMinutes * 60 * 1000
}

export const convertDateToTallinnTimeZone = (date: Date, reverse = false): Date => {
  let tallinnTimeZoneOffset = getTallinnTimeZoneOffsetInMilliseconds(date)
  if (reverse) tallinnTimeZoneOffset = tallinnTimeZoneOffset / -1
  return new Date(date.getTime() + tallinnTimeZoneOffset)
}

export const convertUTCToLocalTime = (utcTimestamp: number): Date => {
  const utcDate = new Date(utcTimestamp)
  const offset = utcDate.getTimezoneOffset()
  return new Date(utcDate.getTime() + offset * 60000)
}

export const convertUTCToLocalDate = (date: Date) => {
  if (!date) {
    return date
  }

  // TODO: Remove this once we migrate to UTC, but for now, convert to 'Europe/Tallinn' time zone.
  date = convertDateToTallinnTimeZone(date)

  date = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )

  return date
}

export const convertLocalToUTCDate = (date: Date) => {
  if (!date) {
    return date
  }

  date = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  )

  // TODO: Remove this once we migrate to UTC, but for now, convert to 'Europe/Tallinn' time zone.
  date = convertDateToTallinnTimeZone(date, true)

  return date
}

/**
 * Get start and end epoch time for current period (usually running month).
 */
export const getTimePeriod = (): { startDate: number; endDate: number } => {
  const today = new Date()
  const startDate = convertLocalToUTCDate(dayjs(today).startOf('month').toDate()).getTime()
  const endDate = convertLocalToUTCDate(dayjs(today).endOf('month').toDate()).getTime()

  return { startDate, endDate }
}

/**
 * Get start and end epoch time for current period (usually running month).
 */
export const getTimePeriodThisMonth = () => {
  const today = new Date()
  const startDate = dayjs(today).startOf('month').toDate()
  const endDate = dayjs(today).endOf('month').toDate()

  return { startDate, endDate }
}

/**
 * Get start and end epoch time for current period (usually running month).
 */
export const getTimePeriodNextMonth = () => {
  const nextMonth = dayjs().add(1, 'month')
  const startDate = nextMonth.startOf('month').toDate()
  const endDate = nextMonth.endOf('month').toDate()

  return { startDate, endDate }
}

/**
 * Get date using a certain format
 * @param date - epoch date
 * @param dateFormat(optional) - date format
 */
export const getDateByFormat = (date: number, dateFormat = 'DD.MM.YY'): string => {
  return dayjs(date).format(dateFormat)
}

/**
 * Get date using a certain format
 *
 * @param date - epoch date
 * @param dateFormat(optional) - date format
 */
export const getDateFormat = (date: Date, dateFormat = 'DD.MM.YY'): string => {
  return dayjs(date).format(dateFormat)
}

// @Deprecated
export const getPeriodStringJsx = (
  startDateNumber: number,
  endDateNumber: number,
  lang?: string,
): JSX.Element | string => {
  if (!startDateNumber || !endDateNumber) {
    return ''
  }

  // TODO: Remove this after full time zone support.
  const startDate = convertUTCToLocalDate(new Date(startDateNumber))
  const endDate = convertUTCToLocalDate(new Date(endDateNumber))

  if (lang === 'EN') {
    const dateFormat = 'MMM D'
    const startYear = dayjs(startDate).year() !== dayjs(endDate).year() ? `, ${dayjs(startDate).format('YYYY')}` : ''
    return (
      <>
        {getDateFormat(startDate, dateFormat)}
        {startYear} - <span style={{ whiteSpace: 'nowrap' }}>{getDateFormat(endDate, 'MMM D, YYYY')}</span>
      </>
    )
  }

  const dateFormat = 'DD.MM'
  const startYear = dayjs(startDate).year() !== dayjs(endDate).year() ? `.${dayjs(startDate).format('YY')}` : ''

  return `${getDateFormat(startDate, dateFormat)}${startYear} - ${getDateFormat(endDate, 'DD.MM.YY')}`
}

export const getPeriodString = (startDateNumber: number, endDateNumber: number, converted = false): string => {
  if (!startDateNumber || !endDateNumber) {
    return ''
  }

  if (!startDateNumber || !endDateNumber) {
    return ''
  }

  // TODO: Remove this after full time zone support.
  const startDate = converted ? new Date(startDateNumber) : convertUTCToLocalDate(new Date(startDateNumber))
  const endDate = converted ? new Date(endDateNumber) : convertUTCToLocalDate(new Date(endDateNumber))

  const dateTimeFormat = new Intl.DateTimeFormat(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
  // @ts-ignore
  return dateTimeFormat.formatRange(startDate, endDate)

  /*const sameYear = getYear(startDate) === getYear(endDate)
  const from = startDate.toLocaleDateString(currentLocaleStringForDate, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })
  const to = endDate.toLocaleDateString(currentLocaleStringForDate, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${from} - ${to}`*/
}

export const getShortDateString = (startDateNumber: number, skipConversion?: boolean): string => {
  if (!startDateNumber) {
    return ''
  }

  // TODO: Remove this after full time zone support.
  const startDate = skipConversion ? new Date(startDateNumber) : convertUTCToLocalDate(new Date(startDateNumber))

  return startDate.toLocaleDateString(currentLocale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

export const getShortDateWithTimeString = (startDateNumber: number): string => {
  if (!startDateNumber) {
    return ''
  }
  return new Date(startDateNumber).toLocaleString(currentLocale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const getShortDateOnlyWithTimeString = (startDateNumber: number): string => {
  if (!startDateNumber) {
    return ''
  }
  return new Date(startDateNumber).toLocaleString(currentLocale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    //hour: '2-digit',
    //minute: '2-digit',
  })
}

export const getShortTimeOnlyWithTimeString = (startDateNumber: number): string => {
  if (!startDateNumber) {
    return ''
  }
  return new Date(startDateNumber).toLocaleString(currentLocale, {
    //month: '2-digit',
    //day: '2-digit',
    //year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const getLongDateString = (date: Date): string => {
  if (!date) {
    return ''
  }
  return date.toLocaleDateString(currentLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const capitalizeFirstLetter = (s: string) => {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const getChallengeDurationInDays = (startDate: number, endDate: number): number => {
  const challengeStartDate = dayjs(convertUTCToLocalDate(new Date(startDate)))
  const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(endDate)))
  return challengeEndDate.diff(challengeStartDate, 'day') + 1
}
